import { TaskResponse } from "@9amhealth/openapi";
import { FC, useMemo } from "react";
import { IconCheckmarkCompleted } from "src/constants/icons";
import translate from "src/lib/translate";
import { TranslationKey } from "src/types/translationKey";
import CollectionItemLink from "../StyledComponents/CollectionItemLink";
import TaskIcons from "./TaskIcons";
import { TaskWithConfig } from "./TaskListBloc";

const TaskListItem: FC<{
  task: TaskWithConfig;
  showBadge?: boolean;
  getTranslationKey: (task: TaskWithConfig) => TranslationKey | "";
  getDescription?: (task: TaskWithConfig) => string;
  handleTaskClick: (task: TaskWithConfig) => void;
}> = ({
  task,
  getTranslationKey,
  getDescription,
  handleTaskClick,
  showBadge
}) => {
  const isCompleted = task.status === TaskResponse.status.COMPLETED;
  const isSkipped = task.status === TaskResponse.status.SKIPPED;

  const badgeType = useMemo((): "completed" | "skipped" | undefined => {
    if (!showBadge) {
      return undefined;
    }

    if (isCompleted) {
      return "completed";
    }

    if (isSkipped) {
      return "skipped";
    }

    return undefined;
  }, [isCompleted, isSkipped]);

  const label = useMemo(() => {
    const key = getTranslationKey(task) || task.uiElements?.title || "";
    return translate(key as TranslationKey, {
      context: `${task.program}.${task.group}`
    });
  }, [task.additionalData?.title, getTranslationKey]);

  const description = useMemo(() => {
    return getDescription ? getDescription(task) : "";
  }, [task, getDescription]);

  return (
    <CollectionItemLink
      key={`${task.program}/${task.group}/${task.slug}`}
      size="large"
      badgeType={badgeType}
      // disable when badge is present
      disabled={Boolean(badgeType)}
      disabledOpacity={1}
      // hide arrow when badge is present
      endIcon={badgeType ? <></> : undefined}
      icon={
        TaskIcons[
          task.additionalData?.iconType ?? task.config?.iconType ?? ""
        ] ?? <IconCheckmarkCompleted />
      }
      onClick={() => {
        handleTaskClick(task);
      }}
      color={
        "linear-gradient(229deg, #ffdfb4 -17.69%, #fddee9 33.52%, #c1d7fb 81.64%)"
      }
      label={<p className="m0 strong">{label}</p>}
      detail={
        description ? (
          <p className="as-little color-c-80 m0">{description}</p>
        ) : undefined
      }
    />
  );
};

export default TaskListItem;
